import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, getInviteCode, isAuthenticated } from "../lib/api";
import type { ProjectSummary, AdminProjectGroup } from "../lib/types";
import Layout from "../components/Layout";

function phaseBadge(project: ProjectSummary) {
  // Inactive badge: interrogation phase with no activity for 7+ days
  if (project.phase === "interrogation" && project.updated_at) {
    const updated = new Date(project.updated_at).getTime();
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (updated && updated < sevenDaysAgo) {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
          Inactive
        </span>
      );
    }
  }
  if (project.phase === "feedback_complete") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
        Complete
      </span>
    );
  }
  if (project.phase === "feedback_active") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
        Feedback In Progress
      </span>
    );
  }
  if (project.phase === "business_case_review") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
        Review Draft
      </span>
    );
  }
  if (project.latest_run_status === "completed") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
        Completed
      </span>
    );
  }
  if (project.latest_run_status === "running") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
        Pipeline Running
      </span>
    );
  }
  if (project.has_brief) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
        Brief Ready
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400">
      In Progress
    </span>
  );
}

function formatDate(dateStr: string) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

const SUPPORTED_LANGUAGES = [
  { value: "English", label: "English" },
  { value: "Spanish", label: "Spanish" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "pt-PT", label: "Portugu\u00eas (Portugal)" },
  { value: "pt-BR", label: "Portugu\u00eas (Brasil)" },
  { value: "Italian", label: "Italian" },
  { value: "Dutch", label: "Dutch" },
  { value: "Polish", label: "Polish" },
  { value: "Swedish", label: "Swedish" },
  { value: "Japanese", label: "Japanese" },
  { value: "Korean", label: "Korean" },
  { value: "Chinese", label: "Chinese" },
];

function getStoredLanguage(): string {
  const stored = localStorage.getItem("architect-language") || "English";
  // Migrate old "Portuguese" value to "pt-PT"
  if (stored === "Portuguese") {
    localStorage.setItem("architect-language", "pt-PT");
    return "pt-PT";
  }
  return stored;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [adminGroups, setAdminGroups] = useState<AdminProjectGroup[]>([]);
  const [adminView, setAdminView] = useState(false);
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [downloadingCode, setDownloadingCode] = useState<string | null>(null);
  const [showNameInput, setShowNameInput] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [language, setLanguageState] = useState(getStoredLanguage);

  // Inline rename state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  // Archive/reactivate loading
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // New project guard modal
  const [guardModal, setGuardModal] = useState<
    | { type: "active_exists"; project: ProjectSummary }
    | { type: "cap_reached" }
    | null
  >(null);
  const [guardArchiving, setGuardArchiving] = useState(false);
  const [guardError, setGuardError] = useState("");

  function setLanguage(lang: string) {
    setLanguageState(lang);
    localStorage.setItem("architect-language", lang);
  }

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/", { replace: true });
      return;
    }

    const code = getInviteCode();
    const verifyPromise = code
      ? api.verifyCode(code)
      : Promise.resolve({ valid: true, runs_remaining: 99, is_admin: false } as { valid: boolean; runs_remaining: number; is_admin: boolean; pilot_tier?: number | null });

    Promise.all([
      verifyPromise,
      api.listProjects(),
    ])
      .then(async ([codeRes, projectsRes]) => {
        if (!codeRes.valid) {
          navigate("/", { replace: true });
          return;
        }
        setIsAdmin(codeRes.is_admin);
        setProjects(projectsRes.projects);
        // Fetch admin grouped view if admin
        if (codeRes.is_admin) {
          try {
            const adminRes = await api.listAdminProjects();
            setAdminGroups(adminRes.groups);
          } catch {
            // Admin endpoint may fail — fall back to regular view
          }
        }
      })
      .catch(() => {
        // Backend might be cold
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  async function handleDeleteProject(e: React.MouseEvent, project: ProjectSummary) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.title || project.goal}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.deleteProject(project.project_id);
      setProjects((prev) => prev.filter((p) => p.project_id !== project.project_id));
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete project");
    }
  }

  async function handleArchive(e: React.MouseEvent, project: ProjectSummary) {
    e.stopPropagation();
    if (!window.confirm("Archive this project? You can reactivate it later.")) return;
    setActionLoading(project.project_id);
    try {
      await api.archiveProject(project.project_id);
      setProjects((prev) =>
        prev.map((p) =>
          p.project_id === project.project_id
            ? { ...p, archived_at: new Date().toISOString() }
            : p,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to archive project");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReactivate(e: React.MouseEvent, project: ProjectSummary) {
    e.stopPropagation();
    const activeProject = projects.find(
      (p) => !p.archived_at && p.customer_tier === "free" && p.project_id !== project.project_id,
    );
    if (activeProject) {
      if (
        !window.confirm(
          `This will archive your current active project "${activeProject.title || activeProject.goal}" and reactivate "${project.title || project.goal}". Continue?`,
        )
      )
        return;
    }
    setActionLoading(project.project_id);
    try {
      const res = await api.reactivateProject(project.project_id);
      setProjects((prev) =>
        prev.map((p) => {
          if (p.project_id === project.project_id) {
            return { ...p, archived_at: null };
          }
          if (res.auto_archived_project_id && p.project_id === res.auto_archived_project_id) {
            return { ...p, archived_at: new Date().toISOString() };
          }
          return p;
        }),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to reactivate project");
    } finally {
      setActionLoading(null);
    }
  }

  function startRename(e: React.MouseEvent, project: ProjectSummary) {
    e.stopPropagation();
    setEditingId(project.project_id);
    setEditTitle(project.title || project.goal);
  }

  async function saveRename(e: React.MouseEvent | React.FormEvent) {
    e.stopPropagation();
    e.preventDefault();
    if (!editingId) return;
    const trimmed = editTitle.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    try {
      await api.renameProject(editingId, trimmed);
      setProjects((prev) =>
        prev.map((p) =>
          p.project_id === editingId ? { ...p, title: trimmed } : p,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to rename project");
    }
    setEditingId(null);
  }

  function cancelRename(e: React.MouseEvent) {
    e.stopPropagation();
    setEditingId(null);
  }

  async function handleExportGroup(e: React.MouseEvent, inviteCode: string) {
    e.stopPropagation();
    setDownloadingCode(inviteCode);
    try {
      await api.exportAdminGroup(inviteCode);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Export failed");
    } finally {
      setDownloadingCode(null);
    }
  }

  // Close guard modal on Escape
  useEffect(() => {
    if (!guardModal) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !guardArchiving) {
        setGuardModal(null);
        setGuardError("");
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [guardModal, guardArchiving]);

  function handleNewProjectClick() {
    // Admin or paid users skip the guard
    const hasNonFree = projects.some(
      (p) => p.customer_tier && p.customer_tier !== "free",
    );
    if (isAdmin || hasNonFree) {
      setShowNameInput(true);
      return;
    }

    const freeProjects = projects.filter(
      (p) => (p.customer_tier || "free") === "free",
    );
    const activeFree = freeProjects.find((p) => !p.archived_at);

    // Lifetime cap: 3 free projects total
    if (freeProjects.length >= 3) {
      setGuardModal({ type: "cap_reached" });
      return;
    }

    // Active free project exists — offer archive-and-swap
    if (activeFree) {
      setGuardModal({ type: "active_exists", project: activeFree });
      setGuardError("");
      return;
    }

    // No guard needed
    setShowNameInput(true);
  }

  async function handleGuardArchiveAndStart() {
    if (guardModal?.type !== "active_exists") return;
    setGuardArchiving(true);
    setGuardError("");
    try {
      await api.archiveProject(guardModal.project.project_id);
    } catch (err) {
      // "already archived" is fine — proceed anyway
      const msg = err instanceof Error ? err.message : "";
      if (!msg.toLowerCase().includes("already archived")) {
        setGuardError(msg || "Failed to archive project. Please try again.");
        setGuardArchiving(false);
        return;
      }
    }
    setProjects((prev) =>
      prev.map((p) =>
        p.project_id === guardModal.project.project_id
          ? { ...p, archived_at: new Date().toISOString() }
          : p,
      ),
    );
    setGuardModal(null);
    setShowNameInput(true);
    setGuardArchiving(false);
  }

  const PIPELINE_PHASES = [
    "business_case_generating", "business_case_review",
    "product_spec_generating", "product_spec_review",
    "architecture_generating", "architecture_review",
    "epic_plan_generating", "epic_plan_review",
    "pipeline_complete",
  ];

  function handleProjectClick(project: ProjectSummary) {
    if (PIPELINE_PHASES.includes(project.phase)) {
      navigate(`/chat?project=${project.project_id}`);
      return;
    }
    if (project.phase === "feedback_active") {
      navigate(`/chat?project=${project.project_id}&feedback=true`);
    } else if (project.phase === "feedback_complete" && project.latest_run_id) {
      navigate(`/artifacts?run_id=${project.latest_run_id}`);
    } else if (project.latest_run_status === "completed" && project.latest_run_id) {
      navigate(`/artifacts?run_id=${project.latest_run_id}`);
    } else if (project.latest_run_status === "running" && project.latest_run_id) {
      navigate(`/pipeline?run_id=${project.latest_run_id}`);
    } else {
      navigate(`/chat?project=${project.project_id}`);
    }
  }

  // Split projects into active and archived
  const activeProjects = projects.filter((p) => !p.archived_at);
  const archivedProjects = projects.filter((p) => !!p.archived_at);

  function renderProjectCard(project: ProjectSummary, isArchived: boolean) {
    const isEditing = editingId === project.project_id;
    const isLoading = actionLoading === project.project_id;
    const isFree = (project.customer_tier || "free") === "free";

    return (
      <div
        key={project.project_id}
        onClick={() => handleProjectClick(project)}
        className={`w-full text-left border rounded-xl p-4 transition-colors cursor-pointer ${
          isArchived
            ? "bg-slate-800/30 border-slate-700/30 hover:bg-slate-800/50 opacity-70"
            : "bg-slate-800/50 hover:bg-slate-800/80 border-slate-700/50 hover:border-slate-600/50"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {isEditing && !isArchived ? (
              <form
                onSubmit={saveRename}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5"
              >
                <input
                  autoFocus
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  maxLength={100}
                  className="bg-slate-700 border border-slate-500 text-white text-sm rounded-lg px-2 py-1 outline-none focus:border-blue-500 flex-1 min-w-0"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setEditingId(null);
                    }
                  }}
                />
                <button
                  type="submit"
                  className="text-green-400 hover:text-green-300 p-0.5"
                  title="Save"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={cancelRename}
                  className="text-slate-400 hover:text-slate-300 p-0.5"
                  title="Cancel"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </form>
            ) : (
              <div className="flex items-center gap-1.5">
                <h3 className="text-sm font-medium text-white truncate">
                  {project.title || project.goal}
                </h3>
                {!isArchived && (
                  <button
                    onClick={(e) => startRename(e, project)}
                    className="text-slate-500 hover:text-slate-300 transition-colors shrink-0"
                    title="Rename"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-1 truncate">
              {project.goal}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {phaseBadge(project)}
            {project.language && project.language !== "English" && (
              <span className="text-xs text-slate-500">
                {project.language}
              </span>
            )}
            <span className="text-xs text-slate-600">
              {formatDate(project.created_at)}
            </span>
            <div className="flex items-center gap-2">
              {isArchived ? (
                <button
                  onClick={(e) => handleReactivate(e, project)}
                  disabled={isLoading}
                  className="text-xs text-blue-400/70 hover:text-blue-400 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? "..." : "Reactivate"}
                </button>
              ) : (
                isFree && (
                  <button
                    onClick={(e) => handleArchive(e, project)}
                    disabled={isLoading}
                    className="text-xs text-slate-500 hover:text-amber-400 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? "..." : "Archive"}
                  </button>
                )
              )}
              {isAdmin && (
                <button
                  onClick={(e) => handleDeleteProject(e, project)}
                  className="text-xs text-red-400/60 hover:text-red-400 transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout showHeader isAdmin={isAdmin}>
      <div className="flex-1 overflow-y-auto px-4 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <h2 className="text-xl sm:text-2xl font-semibold text-white">
                {adminView ? "All Users" : "Your Projects"}
              </h2>
              {isAdmin && adminGroups.length > 0 && (
                <button
                  onClick={() => setAdminView(!adminView)}
                  className="text-xs px-2.5 py-1 rounded-lg border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 transition-colors"
                >
                  {adminView ? "My Projects" : "All Users"}
                </button>
              )}
            </div>
            {!showNameInput ? (
              <button
                onClick={handleNewProjectClick}
                className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-4 py-2 text-sm transition-colors"
              >
                New Project
              </button>
            ) : (
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = newProjectName.trim();
                  if (name) {
                    navigate(`/chat?new=true&name=${encodeURIComponent(name)}&lang=${encodeURIComponent(language)}`);
                  }
                }}
              >
                <input
                  autoFocus
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Project name..."
                  className="bg-slate-800 border border-slate-600 focus:border-blue-500 text-white text-sm rounded-xl px-3 py-2 outline-none w-48"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowNameInput(false);
                      setNewProjectName("");
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!newProjectName.trim()}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl px-4 py-2 text-sm transition-colors"
                >
                  Start
                </button>
              </form>
            )}
          </div>

          {/* Language selector */}
          <div className="flex items-center gap-2 mb-6">
            <label
              htmlFor="lang-select"
              className="text-xs text-slate-500 whitespace-nowrap"
            >
              Content language
            </label>
            <select
              id="lang-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-blue-500 cursor-pointer"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {loading && (
            <div className="text-center py-16 text-slate-500 text-sm">
              Loading projects...
            </div>
          )}

          {/* Admin grouped view */}
          {!loading && adminView && adminGroups.length > 0 && (
            <div className="space-y-4">
              {adminGroups.map((group) => {
                const isExpanded = expandedCodes.has(group.invite_code);
                return (
                  <div
                    key={group.invite_code}
                    className="border border-slate-700/50 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => {
                        setExpandedCodes((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.invite_code)) {
                            next.delete(group.invite_code);
                          } else {
                            next.add(group.invite_code);
                          }
                          return next;
                        });
                      }}
                      className="w-full text-left bg-slate-800/70 hover:bg-slate-800 p-3 flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">
                          {group.invite_code}
                        </span>
                        {group.email && (
                          <span className="text-xs text-slate-500">
                            — {group.email}
                          </span>
                        )}
                        {group.is_admin && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                            admin
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {group.projects.length > 0 && (
                          <button
                            onClick={(e) => handleExportGroup(e, group.invite_code)}
                            disabled={downloadingCode === group.invite_code}
                            className="text-slate-500 hover:text-blue-400 disabled:opacity-50 transition-colors p-1"
                            title="Download all artifacts"
                          >
                            {downloadingCode === group.invite_code ? (
                              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
                              </svg>
                            )}
                          </button>
                        )}
                        <span className="text-xs text-slate-500">
                          {group.projects.length} project{group.projects.length !== 1 ? "s" : ""}
                          {" / "}
                          {group.runs_used}/{group.max_runs} runs
                        </span>
                        <span className="text-xs text-slate-600">
                          {isExpanded ? "\u25B2" : "\u25BC"}
                        </span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-slate-700/30">
                        {group.projects.length === 0 ? (
                          <div className="p-3 text-xs text-slate-600">
                            No projects
                          </div>
                        ) : (
                          group.projects.map((project) => (
                            <button
                              key={project.project_id}
                              onClick={() => handleProjectClick(project)}
                              className="w-full text-left hover:bg-slate-800/50 p-3 border-b border-slate-700/20 last:border-b-0 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <h3 className="text-sm font-medium text-white truncate">
                                    {project.title || project.goal}
                                  </h3>
                                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                                    {project.goal}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  <div className="flex items-center gap-1">
                                    {project.mvp_interest && (
                                      <span title="MVP interest">&#x1F680;</span>
                                    )}
                                    {phaseBadge(project)}
                                  </div>
                                  <span className="text-xs text-slate-600">
                                    {formatDate(project.created_at)}
                                  </span>
                                  <button
                                    onClick={(e) => handleDeleteProject(e, project)}
                                    className="text-xs text-red-400/60 hover:text-red-400 transition-colors"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Regular (non-admin) view */}
          {!loading && !adminView && projects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-400 text-base mb-2">
                No projects yet.
              </p>
              <p className="text-slate-500 text-sm">
                Start your first one by clicking "New Project" above.
              </p>
            </div>
          )}

          {!loading && !adminView && projects.length > 0 && (
            <>
              {/* Active projects */}
              {activeProjects.length > 0 && (
                <div className="space-y-3">
                  {activeProjects.map((project) => renderProjectCard(project, false))}
                </div>
              )}

              {/* Archived projects */}
              {archivedProjects.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-medium text-slate-500 mb-3">
                    Past Projects
                  </h3>
                  <div className="space-y-3">
                    {archivedProjects.map((project) => renderProjectCard(project, true))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* New project guard modal */}
      {guardModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => {
            if (!guardArchiving) {
              setGuardModal(null);
              setGuardError("");
            }
          }}
        >
          <div
            className="bg-slate-800 border border-slate-600 rounded-2xl p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {guardModal.type === "cap_reached" ? (
              <>
                <h3 className="text-lg font-semibold text-white mb-3">
                  Free project limit reached
                </h3>
                <p className="text-slate-300 text-sm mb-6">
                  You've used your 3 free interrogations. Unlock the full assessment on an existing project to keep going.
                </p>
                <button
                  onClick={() => {
                    setGuardModal(null);
                    setGuardError("");
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-4 py-2.5 text-sm transition-colors"
                >
                  View my projects
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-white mb-3">
                  You already have an active project
                </h3>
                <p className="text-slate-300 text-sm mb-6">
                  You can only have one free project at a time. Would you like to archive
                  &ldquo;{guardModal.project.title || guardModal.project.goal}&rdquo; and start fresh?
                </p>
                {guardError && (
                  <p className="text-red-400 text-sm mb-4">{guardError}</p>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleGuardArchiveAndStart}
                    disabled={guardArchiving}
                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
                  >
                    {guardArchiving && (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    Archive and start fresh
                  </button>
                  <button
                    onClick={() => {
                      const pid = guardModal.project.project_id;
                      setGuardModal(null);
                      setGuardError("");
                      navigate(`/chat?project=${pid}`);
                    }}
                    disabled={guardArchiving}
                    className="w-full border border-slate-500 hover:border-slate-400 text-slate-300 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed font-medium rounded-xl px-4 py-2.5 text-sm transition-colors"
                  >
                    Go to my project
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
